import { useCallback, useMemo } from "preact/hooks"
import { useLocation } from "preact-iso"

export function useQueryState<T = string>(
	key: string,
	defaultValue: T,
): [T, (v: T) => void] {
	const { url, route } = useLocation()
	const url2 = new URL(location.origin + url)

	// Parse the current query params
	const params = useMemo(() => new URLSearchParams(url2.search), [url2.search])

	// Read the current value
	const value = useMemo(() => {
		const raw = params.get(key)
		if (raw === null) return defaultValue
		try {
			return JSON.parse(raw) as T
		} catch {
			return raw as unknown as T
		}
	}, [params, key, defaultValue])

	// Setter updates the URL (but not the history stack)
	const setValue = useCallback(
		(next: T) => {
			const newParams = new URLSearchParams(url2.search)

			if (next === defaultValue || next === null || next === undefined) {
				newParams.delete(key)
			} else {
				newParams.set(key, JSON.stringify(next))
			}

			const newUrl =
				url2.pathname + (newParams.toString() ? `?${newParams}` : "")

			// preact-iso router navigation
			route(newUrl, true)
		},
		[url, key, defaultValue, route],
	)

	return [value, setValue]
}
